import React, { useEffect, useState, useRef } from 'react'

import { Box, FormControl, InputLabel, Select, MenuItem, Typography } from '@mui/material'
import { platformIcon } from '../../contact_graph_exception'
import './styles.css'

import GraphToolbar from '../default-toolbar'
import { DataGridPro } from '@mui/x-data-grid-pro'
import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles'
import { graph_theme } from '../default-toolbar'

import LoadingButton from '@mui/lab/LoadingButton';

import PieChart from "../../../../D3/PieChart";
import BarChart from "../../../../D3/BarChart";

import { exportHtmlToImage } from "../../../../../utils/exportHtmlToImage";

import ExportIcon from '@mui/icons-material/Download';

const LOST_CLASSIFICATION_LABELS = {
  OUT_OF_HOURS: 'Perdida - Fora de horário',
  QUEUE_ABANDONMENT: 'Perdida - Abandono de fila',
  TIMEOUT: 'Perdida - Timeout'
}

const LOST_TIMEOUT_DESCRIPTIONS = [
  'Cliente não continuou o chatbot',
  'Cliente não respondeu ao atendimento',
  'Operador não atendeu a chamada'
]

const CLASSIFICATION_COLORS = {
  'Atendida': '#3bae3b',
  'Perdida - Fora de horário': '#b63845',
  'Perdida - Abandono de fila': '#d34c4d',
  'Perdida - Timeout': '#ef6b73',
  'Retida': '#3390ec',
  'Transferência': '#f78f33',
  'Voz': '#f3b681ff'
}

const CLASSIFICATION_HELP = {
  'Atendida': 'Conversa atendida por operador ou finalizada como atendimento válido.',
  'Perdida - Fora de horário': 'Entrada recebida fora do horário de atendimento configurado.',
  'Perdida - Abandono de fila': 'Perdida sem motivo técnico detalhado; agrupada como abandono de fila.',
  'Perdida - Timeout': 'Cliente não continuou ou não respondeu dentro do tempo esperado.',
  'Retida': 'Conversa resolvida no chatbot sem encaminhamento para operador.',
  'Transferência': 'Conversa encaminhada ou transferida entre filas/operadores.',
  'Voz': 'Atendimentos de voz registrados no relatório.'
}

const CLASSIFICATION_ORDER = [
  'Atendida',
  'Perdida - Fora de horário',
  'Perdida - Abandono de fila',
  'Perdida - Timeout',
  'Retida',
  'Transferência',
  'Voz'
]

const normalizeDescription = (description) => (description || '').trim()

const getLostClassificationLabel = (description) => {
  const normalizedDescription = normalizeDescription(description)
  if (normalizedDescription === 'Fora do horário de atendimento')
    return LOST_CLASSIFICATION_LABELS.OUT_OF_HOURS
  if (LOST_TIMEOUT_DESCRIPTIONS.includes(normalizedDescription))
    return LOST_CLASSIFICATION_LABELS.TIMEOUT
  return LOST_CLASSIFICATION_LABELS.QUEUE_ABANDONMENT
}

const getClassificationLabel = (row) => {
  if (! row.classification)
    return 'Voz'
  if (row.classification === 'Perdida')
    return getLostClassificationLabel(row.description)
  if (row.classification === 'Retida no chatbot')
    return 'Retida'
  return row.classification
}

const getReasonDescription = (params) => {
  const row = params.row
  const description = normalizeDescription(row.description)
  if (description)
    return description
  if (row.classification === 'Perdida')
    return getClassificationLabel(row).replace('Perdida - ', '')
  return '-'
}

const getPercentage = (value, total) => total ? `${((value / total) * 100).toFixed(1)}%` : '0.0%'

const getLegendIcon = (platform) => {
  const icon = platformIcon(platform)
  return React.isValidElement(icon) && icon.props?.children ? icon.props.children : icon
}

const getClassificationTooltip = (label, value, total) => {
  const helpText = CLASSIFICATION_HELP[label] || ''
  return `${label}: ${value} (${getPercentage(value, total)}). ${helpText}`
}

const sortClassifications = ([labelA], [labelB]) => {
  const indexA = CLASSIFICATION_ORDER.indexOf(labelA)
  const indexB = CLASSIFICATION_ORDER.indexOf(labelB)
  if (indexA === -1 && indexB === -1)
    return labelA.localeCompare(labelB)
  if (indexA === -1)
    return 1
  if (indexB === -1)
    return -1
  return indexA - indexB
}

const ClassificationGlossary = () => (
  <Box className='chats-channel-glossary'>
    {CLASSIFICATION_ORDER.filter((label) => label !== 'Voz').map((label) => (
      <Box className='chats-channel-glossary-item' key={label}>
        <Box className='chats-channel-glossary-color' sx={{ backgroundColor: CLASSIFICATION_COLORS[label] }} />
        <Box>
          <Typography className='chats-channel-glossary-label' variant='caption'>{label}</Typography>
          <Typography className='chats-channel-glossary-description' variant='caption'>{CLASSIFICATION_HELP[label]}</Typography>
        </Box>
      </Box>
    ))}
  </Box>
)

const columns = graph => {
  const columns = []

  columns.push({
    field: 'platform_entrypoint',
    headerName: 'Canal',
    flex: .5,
    renderCell: (params) => platformIcon(params.value)
  })
  columns.push({
    field: 'classification',
    headerName: 'Classificação',
    flex: .5,
    valueGetter: (params) => params.value || 'Voz'
  })
  columns.push({
    field: 'description',
    headerName: 'Motivo',
    flex: .8,
    valueGetter: getReasonDescription
  })
  columns.push({
    field: 'count',
    headerName: 'Quantidade',
    flex: .5
  })
  return columns
}

const ChatsChannel = ({ data, option, periodText }) => {

  const [channelData, setChannelData] = useState([])
  const [classificationData, setClassificationData] = useState([])
  const [barData, setBarData] = useState([])
  const [dataType, setDataType] = useState("number")

  const containerRef = useRef(null);

  const handleChannelData = (data) => {
    const channels = {}
    data?.rows?.forEach((row) => {
      if (!(row.platform_entrypoint in channels))
        channels[row.platform_entrypoint] = 0
      channels[row.platform_entrypoint] += Number(row.count || 0)
    })
    setChannelData(Object.keys(channels).map((key) => ({
      label: key,
      value: channels[key],
      icon: getLegendIcon(key) ?? undefined
    })))
  }

  const handleClassificationData = (data) => {
    const classifications = {}
    data?.rows?.forEach((row) => {
      const classification = getClassificationLabel(row)
      if (!(classification in classifications))
        classifications[classification] = 0
      classifications[classification] += Number(row.count || 0)
    })
    const total = Object.values(classifications).reduce((sum, value) => sum + value, 0)
    setClassificationData(Object.entries(classifications).sort(sortClassifications).map(([key, value]) => ({
      label: key,
      value,
      tooltip: getClassificationTooltip(key, value, total)
    })))
  }

  const handleBarData = (data) => {
    const bars = {}
    data?.rows?.forEach((row) => {
      const key = `${row.platform_entrypoint}: ${getClassificationLabel(row)}`
      if (!(key in bars))
        bars[key] = 0
      bars[key] += Number(row.count || 0)
    })
    setBarData(Object.entries(bars).map(([label, value]) => ({ label, value })))
  }

  useEffect(() => {
    handleChannelData(data)
    handleClassificationData(data)
    handleBarData(data)
  }, [data])

  return (
    <Box className='graph-main-divisor'>
      <Box className='graph-main-table'>
        <StyledEngineProvider injectFirst>
          <ThemeProvider theme={graph_theme}>
            <DataGridPro
              density='compact'
              columns={columns(option)}
              rows={data?.rows?.map((r, index) => ({ ...r, id: index + 1 })) || []}
              hideFooter={true}
              components={{
                Toolbar: GraphToolbar
              }}
            />
          </ThemeProvider>
        </StyledEngineProvider>
      </Box>
      <Box className='graph-main-dashboards'>
        <Box className='graph-time-rows'>
          {data?.rows?.length > 0 &&
            <>
              <Box sx={{ position: 'absolute', right: '0' }}>
                <FormControl sx={{ width: '200px', position: 'absolute', right: '2rem', zIndex: 1  }}>
                  <InputLabel id="sankey-type-label">Tipo</InputLabel>
                  <Select
                    size="small"
                    labelId="sankey-type-label"
                    id="demo-simple-select"
                    value={dataType}
                    label="Tipo"
                    onChange={(event) => setDataType(event.target.value)}
                  >
                    <MenuItem value={"number"}>Número</MenuItem>
                    <MenuItem value={"percentage"}>Percentual</MenuItem>
                  </Select>
                </FormControl>
                <LoadingButton startIcon={<ExportIcon />}
                  sx={{ position: 'absolute', right: '2rem', top: '3rem',  zIndex: 1  }}
                  onClick={() => exportHtmlToImage(containerRef.current)}
                >
                  Exportar
                </LoadingButton>
              </Box>
            </>
          }
          <Box className='new-graphs-wrapper'>
            <Box ref={containerRef} className='chats-channel-container' style={{ width: "100%", height: "100%", padding: "1rem", position: 'relative' }}>
              <img style={{ width: '10rem', display: 'none', position: 'absolute', right: 0 }} src="/static/media/kwik_color.f6459d28.png" alt="logo" class="logo" id="logo" className='showcopyright'></img>
              <Typography variant="subtitle1">Conversas por canal, {periodText}</Typography>
              <Box className='chats-channel-first-row'>
                <PieChart label='Canais' data={channelData} type={dataType} legendPosition="right" />
                <PieChart label='Classificações' data={classificationData} type={dataType} legendPosition="right" />
              </Box>
              <ClassificationGlossary />
              <Box className='chats-channel-second-row'>
                <BarChart data={barData} type={dataType} />
              </Box>
              <Typography sx={{ display: 'none', position: 'absolute', bottom: 0 }} variant="body2" align="center" className='showcopyright'>
                Copyright (c) {new Date().getFullYear()} - Instant Solutions Tecnologia e Informática Ltda.
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default ChatsChannel
